import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { AlertController, Events, NavParams } from "ionic-angular";
import { WoundModalPage } from "../../../../pages/assessment/wound-modal/wound-modal";
import {
  AppStatus,
  ConnectionStatusEnum,
} from "../../../../providers/app-status/app-status";
import { CanvasService } from "../../canvas/canvas-service.interface";

@Component({
  selector: "wound-site-modal-sidebar",
  templateUrl: "wound-site-modal-sidebar.html",
})
export class WoundSiteModalSidebarComponent implements AfterViewInit {
  @Input() currentStep: number;
  @Input() painService: CanvasService;
  @Input() section: string;
  points = [];

  dataPages: Array<object[]> = [];
  pageSize: number = 10;
  currentPage: number = 0;
  dummyRow: object;
  subscriptions: Array<any> = [];
  appSyncStatus: ConnectionStatusEnum;
  connectionStatusEnum = ConnectionStatusEnum;

  woundType: Array<string> = [
    "burn1deg",
    "burn2deg",
    "burn3deg",
    "burn4deg",
    "bruising",
    "cellulitis",
    "decubitusUlcers",
    "erythema",
    "fragileSkin",
    "mottling",
    "necrosis",
    "tumorNodules",
    "poorTurgor",
    "pressure",
    "rash",
    "skinTears",
    "stasisUlcers",
  ];
  drainage: Array<string> = [
    "purdent",
    "serosanguineour",
    "seropurulent",
    "serous",
  ];
  step1: Array<object> = [
    {
      id: "type",
      options: ["somatic", "visceral", "neuropathic", "unknown", "none"],
    },
    {
      id: "character",
      options: [
        "dull",
        "sharp",
        "burning",
        "radiating",
        "throbbing",
        "cramping",
        "unknown",
        "none",
      ],
    },
    {
      id: "descriptiveSeverity",
      options: ["mild", "moderate", "severe", "unknown", "none"],
    },
    {
      id: "frequency",
      options: ["infrequent", "intermittent", "constant", "unknown", "none"],
    },
    {
      id: "duration",
      options: [
        "seconds",
        "minutes",
        "oneHour",
        "severalHours",
        "onGoing",
        "unknown",
        "none",
      ],
    },
    { id: "onset", options: ["acute", "chronic", "unknown", "none"] },
  ];

  disableLabel: boolean = false;
  painScreeningValues: { cardId: string; value: object; inv: boolean };
  totalPoints: number;

  constructor(
    private cdr: ChangeDetectorRef,
    private alertCtrl: AlertController,
    public translatePipe: TranslateService,
    private woundModalPage: WoundModalPage,
    public appStatus: AppStatus,
    public events: Events,
    public navParams: NavParams
  ) {
    this.painScreeningValues = navParams.get("painScreening");
    this.totalPoints = navParams.get("totalPoint");
    this.appSyncStatus = this.appStatus.getAppStatus();

    this.events.subscribe("networkchange", (eventData) => {
      this.appSyncStatus = eventData.status;
    });
  }

  ngAfterViewInit() {
    this.subscriptions.push(
      this.painService.getAllPointDataObservable().subscribe((data) => {
        // console.log(points);
        if (!data || !data.points) {
          return;
        }

        this.points = data.points;
        for (let i = 0; i < this.points.length; i++) {
          this.points[i].index = i;
          if (this.section == "skin") {
            if (
              this.points[i].woundType === undefined ||
              this.points[i].woundType === null
            ) {
              this.points[i].woundType = {};
            }
            if (
              this.points[i].drainage === undefined ||
              this.points[i].drainage === null
            ) {
              this.points[i].drainage = {};
            }
          }
        }

        this.updateDataPages();
      })
    );
    this.cdr.detectChanges();

    //there will only ever be one point now
    this.addPoint();
    this.selectPoint(0);
  }

  ngOnDestroy() {
    for (let sub of this.subscriptions) {
      sub.unsubscribe();
    }
    this.subscriptions = [];
  }

  woundTypeSelected(point, type, value) {
    if (value) {
      if (type === "normal") {
        for (let item of Object.keys(point["woundType"])) {
          if (item !== "normal") {
            point["woundType"][item] = false;
          }
        }
      } else if (point["woundType"]["normal"]) {
        point["woundType"]["normal"] = false;
      }
    }
  }

  drainageTypeSelected(point, type, value) {
    if (value) {
      if (type === "selectAll") {
        for (let i in this.drainage) {
          let item = this.drainage[i];
          point["drainage"][item] = item !== "none";
        }
        point["drainage"]["none"] = false;
      } else if (type === "none") {
        for (let item of Object.keys(point["drainage"])) {
          if (item !== "none") {
            point["drainage"][item] = false;
          }
        }
        point["drainage"]["selectAll"] = false;
      } else if (point["drainage"]["none"]) {
        point["drainage"]["none"] = false;
      }
    } else {
      if (type !== "selectAll" && type !== "none") {
        point["drainage"]["selectAll"] = false;
      }
    }
  }

  selectPoint(index) {
    this.painService.selectPoint(index);
  }

  setCategory(category) {
    this.painService.setCategory(category);
  }

  addPoint() {
    if (this.totalPoints === 0) {
      if (
        this.painScreeningValues &&
        this.painScreeningValues.inv &&
        this.painScreeningValues.inv === true &&
        this.painScreeningValues.value["severity"] !== null
      ) {
        let defaultPainScreening = {
          category: "body",
          painTool: this.painScreeningValues.value["painTool"],
          index: 0,
          active: true,
          unableToScore: null,
          painValues: this.painScreeningValues.value["painValues"],
          severity: this.painScreeningValues.value["severity"],
        };
        this.painService.addDefaultPoint(defaultPainScreening);
      } else {
        this.painService.addPoint();
      }
    } else {
      this.painService.addPoint();
    }
  }

  removePoint(index) {
    let alert = this.alertCtrl.create({
      title: this.translatePipe.instant("confirmDeleteRecord"),
      buttons: [
        {
          text: "Cancel",
        },
        {
          text: "Proceed",
          handler: () => {
            this.painService.removePoint(index);
            this.painService.addPoint();
            this.selectPoint(0);
          },
        },
      ],
    });
    alert.present();
  }

  updateActivePointData(key, value) {
    this.painService.updateActivePointData(key, value);
  }

  updateDataPages() {
    this.dataPages = [];
    this.dummyRow = { x: "", y: "", dummy: true };

    let dataRows = this.points;
    let pageSize = this.pageSize;
    let dataPages = this.dataPages;
    let pageIndex = -1;
    for (let i = 0; i < dataRows.length; i++) {
      let j = i % pageSize;
      if (j == 0) {
        dataPages.push([]);
        pageIndex++;
      }
      dataPages[pageIndex][j] = dataRows[i];
    }
    if (pageIndex > 0) {
      let endingRow = dataRows.length % this.pageSize;
      if (endingRow > 0) {
        for (let i = endingRow; i < this.pageSize; i++) {
          dataPages[pageIndex][i] = this.dummyRow;
        }
      }
    }

    //reset to first page if active element is deleted
    let newCurrentPage = 0;
    for (let i = 0; i < this.points.length; i++) {
      if (this.points[i].active) {
        newCurrentPage = Math.floor(i / pageSize);
      }
    }
    this.currentPage = newCurrentPage;
  }

  prevPage() {
    this.currentPage--;
  }

  nextPage() {
    this.currentPage++;
  }

  isPointDefined(point) {
    let coordsDefined =
      point.x !== undefined &&
      point.x !== null &&
      point.y !== undefined &&
      point.y !== null &&
      point.category !== undefined &&
      point.category != null;
    let unspecifiedLocation = point.unspecifiedLocation;

    return coordsDefined || unspecifiedLocation;
  }

  painToolsDefined(point) {
    let painValuesComplete = true;
    for (let attr in point["painValues"]) {
      if (
        point["painValues"][attr] === undefined ||
        point["painValues"][attr] === null
      ) {
        painValuesComplete = false;
        break;
      }
    }

    let painToolDefined: boolean =
      painValuesComplete &&
      point.painTool !== undefined &&
      point.painTool !== null &&
      point.severity !== undefined &&
      point.severity !== null;

    let unableToScore: boolean = point.unableToScore;

    return painToolDefined || unableToScore === true;
  }

  isOffline() {
    return this.appSyncStatus === this.connectionStatusEnum.Offline;
  }

  isStepComplete() {
    if (this.isOffline()) {
      this.currentStep = this.woundModalPage.currentStep;
    }

    if (this.points.length == 0) {
      return false;
    }

    if (this.section == "pain") {
      if (this.currentStep == 1) {
        for (let i = 0; i < this.points.length; i++) {
          let point = this.points[i];
          if (!this.painToolsDefined(point)) {
            return false;
          }
        }
        return true;
      } else if (this.currentStep == 2) {
        for (let i = 0; i < this.points.length; i++) {
          let point = this.points[i];
          let pointNamed =
            point.name === undefined ||
            point.name === null ||
            point.name.length === 0;
          let pointDefined = !this.isPointDefined(point);
          if (this.section == "pain") {
            let pointUnspecifiedLocation = point.unspecifiedLocation;
            let pointUnableToDetermine = point.unableToDetermine;
            let pointNoPain = point.noPain;
            let pointCharacter =
              point.character === undefined || point.character === null;
            let pointDescriptiveSeverity =
              point.descriptiveSeverity === undefined ||
              point.descriptiveSeverity === null;
            let pointDuration =
              point.duration === undefined || point.duration === null;
            let pointFrequency =
              point.frequency === undefined || point.frequency === null;
            let pointOnset = point.onset === undefined || point.onset === null;
            let pointType = point.type === undefined || point.type === null;
            let pointX = point.x === undefined || point.x === null;
            let pointY = point.y === undefined || point.y === null;
            if (
              pointUnspecifiedLocation ||
              pointUnableToDetermine ||
              pointNoPain
            ) {
              if (
                pointCharacter ||
                pointDescriptiveSeverity ||
                pointDuration ||
                pointFrequency ||
                pointOnset ||
                pointType
              ) {
                return false;
              } else {
                return true;
              }
            } else {
              if (
                pointDefined ||
                pointNamed ||
                pointCharacter ||
                pointDescriptiveSeverity ||
                pointDuration ||
                pointFrequency ||
                pointOnset ||
                pointType ||
                pointX ||
                pointY
              ) {
                return false;
              } else {
                return true;
              }
            }
          } else {
            let pointDefinedAndNamed =
              !this.isPointDefined(point) || pointNamed;
            if (point.severity !== 0 && pointDefinedAndNamed) {
              return false;
            }
          }
        }
        return true;
      } else if (this.currentStep == 3) {
        //The questions weren't designed to be required via config so we'll do it here
        if (this.section === "pain") {
          for (let i = 0; i < this.points.length; i++) {
            let point = this.points[i];

            let answer1 = point.painQuestion1 && point.painQuestion1.length > 0,
              answer2 = point.painQuestion2 && point.painQuestion2.length > 0,
              answer3 = point.painQuestion3 && point.painQuestion3.length > 0;
            if (!answer1 || !answer2 || !answer3) {
              return false;
            }
          }
        }
        return true;
      }
      return false;
    } else {
      if (this.currentStep == 2) {
        return true;
      } else if (this.currentStep == 1) {
        for (let i = 0; i < this.points.length; i++) {
          let point = this.points[i];
          let pointNamed =
            point.name === undefined ||
            point.name === null ||
            point.name.length === 0;
          let pointDefined = !this.isPointDefined(point);
          if (this.section == "skin") {
            let pointUnspecifiedLocation = point.unspecifiedLocation;
            if (pointUnspecifiedLocation) {
              return true;
            } else if (pointDefined || pointNamed) {
              return false;
            } else {
              return true;
            }
          } else {
            let pointDefinedAndNamed =
              !this.isPointDefined(point) || pointNamed;
            if (point.severity !== 0 && pointDefinedAndNamed) {
              return false;
            }
          }
        }
        return true;
      } else if (this.currentStep == 3) {
        return true;
      }
      return false;
    }
  }

  setUnspecifiedLocation(point) {
    if (this.disableLabel) this.disableLabel = false;
    else this.disableLabel = true;

    this.painService.updateActivePointData(
      "unspecifiedLocation",
      point.unspecifiedLocation
    );
  }

  setName() {
    //this.painService.clearDropDownsValue(0);
  }

  setUnableToScore(point) {
    this.painService.updateActivePointData(
      "unableToScore",
      point.unableToScore
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/configurable/cards/wound-site-modal-sidebar/wound-site-modal-sidebar.ts